import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle, ArrowRight, Heart } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Accept = () => {
  const { appId } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";

  const [stage, setStage] = useState("loading"); // loading | ready | already | invalid | done
  const [offer, setOffer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!token) {
        setStage("invalid");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/pilot/accept-check/${appId}`, { params: { t: token } });
        if (cancel) return;
        if (!data.valid) {
          setStage("invalid");
          return;
        }
        setOffer(data);
        setStage(data.already_accepted ? "already" : "ready");
      } catch {
        if (!cancel) setStage("invalid");
      }
    };
    run();
    return () => { cancel = true; };
  }, [appId, token]);

  const accept = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pilot/accept-alternative/${appId}`, { token });
      setOffer((o) => ({ ...o, ...data }));
      setStage("done");
      toast.success(`You've accepted the $${data.amount} Float.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept the offer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="pilot-accept-page">
      <header className="max-w-[1200px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="accept-back-home">
          <LogoMark />
          <span className="font-display font-extrabold tracking-tight">GigAfloat</span>
        </Link>
        <Link to="/" className="text-xs text-white/45 hover:text-white font-mono-c">← back to landing</Link>
      </header>

      <main className="max-w-[680px] mx-auto px-6 lg:px-12 py-12">
        {stage === "loading" && (
          <div className="py-24 text-center text-white/40">
            <Loader2 className="w-7 h-7 animate-spin inline" />
          </div>
        )}

        {stage === "invalid" && (
          <div className="rounded-3xl border border-red-500/30 bg-red-500/[0.04] p-8 text-center" data-testid="accept-invalid">
            <AlertTriangle className="w-9 h-9 text-red-300 mx-auto mb-3" />
            <h1 className="font-display text-2xl font-extrabold mb-2">This offer link is invalid</h1>
            <p className="text-white/55 text-sm">
              The link may have expired or been used already. If you believe this is a mistake, just reply to the email we sent.
            </p>
            <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-[#00FF88] hover:text-white text-sm font-bold">
              Back to GigAfloat <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {stage === "ready" && offer && (
          <div data-testid="accept-ready">
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// Alternative float offer</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">
              Hey {offer.first_name || "there"} <Heart className="inline w-7 h-7 text-[#00FF88] fill-[#00FF88] -mt-1" />
            </h1>
            <p className="text-white/65 text-base leading-relaxed mb-8">
              We&apos;ve approved you for a <strong className="text-[#00FF88]">${offer.amount} Float</strong> through the GigAfloat Founder Pilot Program.
              Once you accept, we&apos;ll send instructions for receiving it.
            </p>

            <div className="rounded-2xl border border-[#00FF88]/30 bg-[#00FF88]/[0.04] p-6 mb-8">
              <div className="text-[10px] font-mono-c uppercase tracking-[0.2em] text-[#00FF88] mb-1">Approved amount</div>
              <div className="font-display font-black text-4xl">${offer.amount}</div>
              <div className="text-xs text-white/55 mt-3">
                Repayment window: <strong className="text-white">{offer.amount === 25 ? "3" : "5"} calendar days</strong> after funds are sent · No interest · No late fees · Founder-led.
              </div>
            </div>

            <button
              onClick={accept}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00FF88] text-black font-bold text-base px-6 py-4 rounded-full btn-glow disabled:opacity-50"
              data-testid="accept-confirm-btn"
            >
              {submitting
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : <>Accept My ${offer.amount} Float <ArrowRight className="w-4 h-4" /></>}
            </button>

            <p className="text-[11px] text-white/35 text-center mt-4 font-mono-c">
              // By accepting you agree to repay ${offer.amount} via the universal Square link within {offer.amount === 25 ? "3" : "5"} calendar days of receiving the funds.
            </p>
          </div>
        )}

        {(stage === "already" || stage === "done") && offer && (
          <div className="rounded-3xl border border-[#00FF88]/30 bg-[#00FF88]/[0.04] p-10 text-center" data-testid="accept-done">
            <div className="w-14 h-14 rounded-full bg-[#00FF88] text-black grid place-items-center mx-auto mb-4">
              <Check className="w-7 h-7" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-black tracking-tighter mb-2">
              {stage === "done" ? `You're in, ${offer.first_name || "friend"}!` : "Already accepted"}
            </h1>
            <p className="text-white/65 text-sm mb-6">
              You&apos;ve accepted the <strong className="text-[#00FF88]">${offer.amount} Float</strong>. We&apos;ll email you with the funding details shortly. Keep an eye on your inbox — and reply if anything comes up.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00FF88]/40 text-white hover:text-[#00FF88] font-bold text-sm px-5 py-2.5 rounded-full"
              data-testid="accept-back-link"
            >
              Back to GigAfloat <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default Accept;
